import {
  AccordionContent,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionTitleText,
  AccordionTrigger,
} from "ui/gluestack/accordion";
import { ChevronDownIcon, ChevronUpIcon } from "ui/gluestack/icon";
import { Text, View } from "react-native";
import { BodyMapEntryModel } from "../../../services/api/types/bodyMap/models/BodyMapEntryModel";
import { format } from "date-fns";
import { BodyMap } from "./BodyMap";
import ViewDocuments from "../../../components/molecules/ViewDocuments";
import { useEffect, useRef, useState } from "react";
import {
  useLazyGetBodyMapEntryQuery,
  useResolveBodyMapEntryMutation,
} from "../../../services/api/bodyMapApi";
import { Button, ButtonText } from "ui/gluestack/button";
import { UpdateBodyMapEntryModal } from "../modals/UpdateBodyMapEntryModal";
import { BodyMapEntryUpdate } from "../molecules/BodyMapEntryUpdate";
import { BodyMapEntryStatusEnums } from "../../../services/api/types/bodyMap/BodyMapEntryStatusEnums";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { PermissionsEnums } from "../../../services/api/types/authorisation/enums/PermissionsEnums";
import { PermissionCheck } from "../../permission/atoms/PermissionCheck";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";

interface Props {
  entry: BodyMapEntryModel;
  isOpen: boolean;
}

export function BodyMapEntryItem({ entry, isOpen }: Props) {
  const ref = useRef<React.ComponentRef<typeof UpdateBodyMapEntryModal>>(null);
  const handleShowModal = () => {
    ref.current?.show();
  };

  const [hasFetched, setHasFetched] = useState(false);

  const { showErrorToast, showNewToast } = useDisplayToast();

  const [trigger, { data, isFetching }] = useLazyGetBodyMapEntryQuery();

  useEffect(() => {
    if (isOpen && !hasFetched) {
      trigger({ residentId: entry.resident_id, entryId: entry.id });
      setHasFetched(true);
    }
  }, [isOpen, hasFetched, trigger, entry.resident_id, entry.id]);

  const [triggerResolve, { isLoading }] = useResolveBodyMapEntryMutation();

  const handleResolveBodyMapEntry = async () => {
    try {
      await triggerResolve({
        residentId: entry.resident_id,
        entryId: entry.id,
      }).unwrap();
      showNewToast({
        title: "Body Map Entry Resolved",
        message: "Body map entry resolved successfully",
        type: "success",
      });
    } catch (error) {
      showErrorToast(error);
    }
  };

  return (
    <View>
      <AccordionItem
        key={entry.id}
        value={`${entry.id}`}
        className="border border-secondary-200 rounded-xl"
      >
        <AccordionHeader>
          <AccordionTrigger className="focus:web:rounded-lg">
            {({ isExpanded }) => (
              <>
                <View className="gap-2">
                  <AccordionTitleText className="font-medium text-primary-700">
                    <Text className="capitalize">{entry.condition}</Text>
                  </AccordionTitleText>
                  <Text className="text-sm font-normal text-typography-600">
                    {format(
                      new Date(entry.created_at),
                      "dd-MM-yyyy 'at' HH:mm",
                    )}
                  </Text>
                </View>
                {isExpanded ? (
                  <AccordionIcon as={ChevronUpIcon} />
                ) : (
                  <AccordionIcon as={ChevronDownIcon} />
                )}
              </>
            )}
          </AccordionTrigger>
        </AccordionHeader>

        <AccordionContent>
          {isFetching ? (
            <LoadingSpinner />
          ) : (
            <>
              <BodyMap selectedParts={data?.location ?? []} />
              <View className="gap-2">
                <Text className="text-base font-medium text-typography-900">
                  Description
                </Text>
                <Text className="text-base font-normal text-typography-900">
                  {data?.initial_comment}
                </Text>
              </View>

              <ViewDocuments documents={data?.images ?? []} className="mt-5" />

              <View className="border-b border-gray-200 w-full my-8" />

              <View className="mb-8 gap-2">
                <Text className="text-base font-medium text-typography-900">
                  Treatment Suggested
                </Text>
                <Text className="text-base font-normal text-typography-800">
                  {data?.treatment_plan}
                </Text>
              </View>

              <View className="gap-2">
                <Text className="text-base font-medium text-typography-900">
                  Task Frequency
                </Text>
                <Text className="text-base font-normal text-typography-800">
                  {data?.task_frequency}
                </Text>
              </View>
              <View className="mt-16">
                {data?.logs?.map((log) => (
                  <View key={log.id} className="mb-3">
                    <BodyMapEntryUpdate log={log} />
                  </View>
                ))}
              </View>
              {entry.status !== BodyMapEntryStatusEnums.RESOLVED ? (
                <PermissionCheck
                  permission={PermissionsEnums.UPDATE_BODY_MAP_ENTRY}
                >
                  <View className="flex-row ml-auto gap-3">
                    <Button
                      action="primary"
                      variant="outline"
                      size="sm"
                      onPress={handleResolveBodyMapEntry}
                    >
                      <ButtonText className="text-xs sm:text-base">
                        Mark as resolved
                      </ButtonText>
                    </Button>
                    <Button
                      action="primary"
                      size="sm"
                      onPress={handleShowModal}
                      disabled={isLoading}
                    >
                      <ButtonText className="text-xs sm:text-base">
                        Add new update
                      </ButtonText>
                    </Button>
                  </View>
                </PermissionCheck>
              ) : null}
            </>
          )}
        </AccordionContent>
      </AccordionItem>
      <UpdateBodyMapEntryModal
        residentId={entry.resident_id}
        entryId={entry.id}
        locations={entry.location}
        ref={ref}
      />
    </View>
  );
}
